/**
 * Multi-currency conversion helpers.
 *
 * Supports two calling conventions for backward compatibility:
 *
 *   1. Legacy (EUR/GBP only):
 *      getConversionRate('EUR', 'GBP', 0.855)
 *      where the 3rd arg is a single number (EUR_GBP rate).
 *
 *   2. Multi-currency (DKK, USD, etc.):
 *      getConversionRate('DKK', 'GBP', { EUR: 0.855, DKK: 0.115, USD: 0.79 })
 *      where the 3rd arg is an object mapping currency codes to their
 *      GBP multiplier (1 unit of currency = N GBP).
 *
 * The rates object uses the same shape returned by the backend in
 * fx_rates_used (keys like "EUR_GBP", "DKK_GBP") or the raw shape
 * { EUR: rate, DKK: rate }.
 *
 * Conversion path: from -> GBP (multiply by fromRate) -> to (divide by toRate).
 * GBP is always rate 1.
 */

/**
 * Parse the 3rd argument into a { currency: rateToGBP } map.
 * Handles:
 *   - a number (legacy EUR_GBP scalar)
 *   - an object with keys like "EUR_GBP", "DKK_GBP" (backend fx_rates_used shape)
 *   - an object with keys like "EUR", "DKK" (raw rates shape)
 */
function _parseRates(ratesOrScalar) {
  if (ratesOrScalar == null) return null;

  if (typeof ratesOrScalar === 'number') {
    if (ratesOrScalar <= 0) return null;
    return { GBP: 1, EUR: ratesOrScalar };
  }

  if (typeof ratesOrScalar === 'object') {
    const map = { GBP: 1 };
    for (const [key, val] of Object.entries(ratesOrScalar)) {
      if (key === 'as_of' || key === 'provenance') continue;
      const rate = parseFloat(val);
      if (!rate || rate <= 0) continue;

      // Handle "EUR_GBP" style keys (extract the base currency before _GBP)
      const match = key.match(/^([A-Z]{3})_GBP$/);
      if (match) {
        map[match[1]] = rate;
      } else if (/^[A-Z]{3}$/.test(key)) {
        // Handle "EUR", "DKK" style keys
        map[key] = rate;
      }
    }
    return Object.keys(map).length > 1 ? map : null;
  }

  return null;
}

/**
 * Return the scalar multiplier that converts `fromCurrency` to `toCurrency`.
 *
 * @param {string} fromCurrency   e.g. 'GBP', 'EUR', 'DKK'
 * @param {string} toCurrency     e.g. 'GBP', 'EUR'
 * @param {number|object} ratesOrEurGbp  EUR_GBP rate (number) or rates map (object)
 * @returns {number} multiplier (1 when no conversion needed or rate unavailable)
 */
export function getConversionRate(fromCurrency, toCurrency, ratesOrEurGbp) {
  if (fromCurrency === toCurrency) return 1;

  const rates = _parseRates(ratesOrEurGbp);
  if (!rates) return 1;

  const fromRate = rates[fromCurrency];
  const toRate = rates[toCurrency];

  // Both currencies must have known rates to GBP
  if (!fromRate || !toRate || fromRate <= 0 || toRate <= 0) return 1;

  // from -> GBP -> to
  return fromRate / toRate;
}

/**
 * Like getConversionRate, but never falls back to an identity (1:1) rate
 * when the pair is unresolvable -- returns null instead so the caller can
 * treat the amount as genuinely unconvertible (CLAUDE.md section 3: "a
 * missing rate ... never falls back to 1.0"). getConversionRate's identity
 * fallback stays as-is for its existing callers (changing that shared
 * default is out of scope here and would need its own blast-radius audit);
 * this is an additive export for call sites -- like the Bank Transactions
 * cash-flow chart -- that must never silently naked-sum an unconvertible
 * currency into a converted total.
 *
 * @returns {number|null} the multiplier, or null if no real rate is known
 */
export function getConversionRateStrict(fromCurrency, toCurrency, ratesOrEurGbp) {
  if (fromCurrency === toCurrency) return 1;

  const rates = _parseRates(ratesOrEurGbp);
  if (!rates) return null;

  const fromRate = rates[fromCurrency];
  const toRate = rates[toCurrency];
  if (!fromRate || !toRate || fromRate <= 0 || toRate <= 0) return null;

  return fromRate / toRate;
}

/**
 * Convert a monetary value from one currency to another.
 *
 * @param {number|null|undefined} value       The amount to convert
 * @param {string} fromCurrency               e.g. 'GBP', 'EUR', 'DKK'
 * @param {string} toCurrency                 e.g. 'GBP', 'EUR'
 * @param {number|object} ratesOrEurGbp       EUR_GBP rate or rates map
 * @returns {number|null|undefined} converted value (null/undefined pass through)
 */
export function convertAmount(value, fromCurrency, toCurrency, ratesOrEurGbp) {
  if (value == null) return value;
  return value * getConversionRate(fromCurrency, toCurrency, ratesOrEurGbp);
}

/**
 * Return a display label indicating whether the value is native or approximate.
 *
 * @param {string} nativeCurrency  The source currency of the value
 * @param {string} displayCurrency The target display currency
 * @returns {string} e.g. 'GBP', 'EUR', 'GBP approx', 'EUR approx'
 */
export function currencyLabel(nativeCurrency, displayCurrency) {
  if (nativeCurrency === displayCurrency) return displayCurrency;
  return `${displayCurrency} approx`;
}

/**
 * Resolve the parent (net) amount and its currency symbol for a grouped
 * intercompany position row.
 *
 * The parent net is the server-converted total (converted_net) whenever a
 * conversion exists, so it MUST render under the display-currency symbol, never
 * under a native sub-row currency.  This is the single fix for the "converted
 * GBP magnitude shown with a EUR symbol" bug: the currency symbol always
 * matches the display-currency toggle for the parent row.
 *
 * Only when there is no conversion and a single native currency does the parent
 * fall back to that native amount/currency.  With multiple native currencies
 * and no conversion we return a null amount (never sum across currencies).
 *
 * @param {object} group           grouped position row from the API
 * @param {string} displayCurrency current display-currency toggle (e.g. 'GBP')
 * @returns {{amount: number|null, currency: string}}
 */
export function resolveIntercompanyParentNet(group, displayCurrency) {
  if (group && group.converted_net != null) {
    return {
      amount: group.converted_net,
      currency: group.display_currency || displayCurrency,
    };
  }
  const currencies = (group && group.currencies) || [];
  if (currencies.length === 1) {
    return { amount: currencies[0].native_amount, currency: currencies[0].currency };
  }
  return { amount: null, currency: displayCurrency };
}
